import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import graphColumnsConfig from '@/components/features/config/columns/sensors/graph.column.config'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import LineCharts from './LineCharts'
import dayjs, { Dayjs } from 'dayjs'
import AppDateRangePicker from '@/components/DatePickers/AppDateRangePicker'
import { useGridApiRef } from '@mui/x-data-grid-premium'
import { getAllSensors } from '@/local_db/db'
import { useAppTheme } from '@/theme/ThemeContext'
import { debugLog } from '@/utils/debugLog'
function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min)) + min
}
type LocState = { type: string; ids: (string | number)[] }

const getId = (row: any) => row.id ?? row._id ?? row.key ?? row.uid ?? row.guid
const colorsMap = new Map<string, string>([])
const colorFor = (id: string | number) => {
  const s = String(id)
  if (colorsMap.has(s)) return colorsMap.get(s)!

  // Hash to produce a deterministic hue (0–360)
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) % 360
  }

  // Use hash for hue, but also derive saturation and lightness for more variety
  const hue = (getRandomInt(1, 1000) + hash) % 360
  const saturation = getRandomInt(40, 70) // 50–100%
  const lightness = getRandomInt(20, 40) // 40–60%

  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`
  colorsMap.set(id as string, color)
  return color
}

const fmtDate = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(
    d.getFullYear()
  ).slice(-2)}`

const daysDiffInclusive = (a: Date, b: Date) => {
  const MS = 24 * 60 * 60 * 1000
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime()
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime()
  return Math.max(1, Math.round((end - start) / MS) + 1)
}

const buildDateLabels = (n: number, startDate?: Date, endDate?: Date) => {
  if (startDate && endDate && endDate >= startDate) {
    const out: string[] = []
    const MS = 24 * 60 * 60 * 1000
    const start = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    ).getTime()
    for (let i = 0; i < n; i++) {
      out.push(fmtDate(new Date(start + i * MS)))
    }
    return out
  }
  const end = endDate ?? new Date()
  const out: string[] = []
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(end)
    d.setDate(d.getDate() - i)
    out.push(fmtDate(d))
  }
  return out
}

const buildChartData = (
  rows: any[],
  startDate?: Dayjs | null,
  endDate?: Dayjs | null
) => {
  const maxLen = Math.max(...rows.map(r => r.dailyUsageData?.length ?? 0), 0)
  if (maxLen === 0) return []

  let dates: string[]

  if (startDate && endDate && endDate >= startDate) {
    const len = daysDiffInclusive(startDate.toDate(), endDate.toDate())
    dates = buildDateLabels(len, startDate.toDate(), endDate.toDate())
  } else {
    dates = buildDateLabels(
      maxLen,
      startDate?.toDate(),
      endDate?.toDate() ?? undefined
    )
  }

  return dates.map((label, idx) => {
    const point: Record<string, number | string | null> = { date: label }
    rows.forEach(r => {
      const arr: number[] = r.dailyUsageData ?? []
      const key = getId(r)
      point[key] = idx < arr.length ? arr[idx] : null
    })
    return point
  })
}

type RowSelectionModelV7 = {
  type: 'include' | 'exclude'
  ids: Set<string | number>
}

const SensorsGraph: React.FC = () => {
  const [sensors, setSensors] = useState<any[]>([])
  const [sensorsLoading, setSensorsLoading] = useState(true)
  const apiRefExternal = useGridApiRef()
  const theme = useTheme()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const navigate = useNavigate()
  const { state } = useLocation() as { state: LocState | undefined }

  // Dark mode colors
  const colors = {
    filterBarBg: isDarkMode ? '#1A2942' : '#F7FAFD',
    btnBg: isDarkMode ? '#3B82F6' : '#E3F5FF',
    btnHover: isDarkMode ? '#2563EB' : '#D4EDFF',
    btnText: isDarkMode ? '#fff' : '#003F61',
    btnBorder: isDarkMode ? '#3B82F6' : '#E3F5FF',
    outlineBtnBorder: isDarkMode ? '#445876' : '#E4E7EB',
    paperBorder: isDarkMode ? '#445876' : '#E4E7EB',
    paperBg: isDarkMode ? '#162645' : '#fff',
    titleColor: isDarkMode ? '#fff' : '#000000DE',
    titleBorder: isDarkMode ? '#445876' : '#323B771A',
    arrowColor: isDarkMode ? '#93A3B5' : '#C0C2D6',
    dashColor: isDarkMode ? '#93A3B5' : '#6B778C',
    labelColor: isDarkMode ? '#93A3B5' : undefined,
    inputBg: isDarkMode ? '#162645' : undefined,
    inputBorder: isDarkMode ? '#445876' : undefined,
    calendarBg: isDarkMode ? '#162645' : '#fff',
    menuBg: isDarkMode ? '#162645' : undefined,
    menuItemHover: isDarkMode ? '#202B40' : undefined,
  }

  const [typeFilter, setTypeFilter] = React.useState<string>('pressure')
  const [startDate, setStartDate] = React.useState<Dayjs | null>(
    dayjs().subtract(7, 'day')
  )
  const [endDate, setEndDate] = React.useState<Dayjs | null>(dayjs())

  React.useEffect(() => {
    if (!state || !state.ids?.length) navigate(-1)
  }, [state, navigate])

  useEffect(() => {
    fetchSensors()
  }, [])

  const fetchSensors = async () => {
    setSensorsLoading(true)
    const data = await getAllSensors()
    debugLog(data)
    setSensors(data)
    setSensorsLoading(false)
  }

  const allowedRows = React.useMemo(() => {
    const idsSet = new Set(state?.ids ?? [])
    for (const s of idsSet) {
      colorFor(s as string)
    }
    return sensors.filter(r => idsSet.has(getId(r)))
  }, [state, sensors])

  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<RowSelectionModelV7>({
      type: 'include',
      ids: new Set<string | number>(),
    })

  React.useEffect(() => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set(allowedRows.map(getId)),
    })
  }, [allowedRows])

  const effectiveIncludedIds = React.useMemo(() => {
    const allIds = allowedRows.map(getId)
    const allSet = new Set(allIds)
    const model = rowSelectionModel
    if (!model || !(model.ids instanceof Set)) return allSet
    if (model.type === 'include') return model.ids.size ? model.ids : allSet
    const included = new Set(allSet)
    model.ids.forEach(id => included.delete(id))
    return included
  }, [rowSelectionModel, allowedRows])

  const rowsForChart = React.useMemo(
    () => allowedRows.filter(r => effectiveIncludedIds.has(getId(r))),
    [allowedRows, effectiveIncludedIds]
  )

  const chartData = React.useMemo(
    () => buildChartData(rowsForChart, startDate, endDate),
    [rowsForChart, startDate, endDate]
  )

  const handleSearch = () => {
    setEndDate(prev => (prev ? dayjs(prev) : null))
  }

  const chartSeries = React.useMemo(
    () =>
      rowsForChart.map(r => {
        const id = getId(r)
        const key = id
        return {
          data: chartData.map(p => {
            return typeof p[key] === 'object' ? (p[key] as any)?.value : null
          }),
          color: colorsMap.get(id),
          connectNulls: true,
        }
      }),
    [rowsForChart, chartData]
  )

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageHeaderWithIcons title="תוצאות גרף" showBackArrow backTo="/sensors" />
      <Box
        sx={{
          width: '100%',
          direction: 'rtl',
          bgcolor: colors.filterBarBg,
          p: 1.5,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: '100%',
            direction: 'rtl',
            bgcolor: colors.filterBarBg,
            p: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          <FormControl size="small" sx={{ 
            minWidth: 190,
            '& .MuiInputLabel-root': { color: colors.labelColor },
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.inputBg,
              '& fieldset': { borderColor: colors.inputBorder },
            },
            '& .MuiSelect-icon': { color: colors.labelColor },
          }}>
            <InputLabel id="edge-type-label">סוג יח. קצה</InputLabel>
            <Select
              labelId="edge-type-label"
              value={typeFilter}
              label="סוג יח. קצה"
              onChange={e => setTypeFilter(e.target.value as string)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: colors.menuBg,
                    '& .MuiMenuItem-root:hover': { bgcolor: colors.menuItemHover },
                  },
                },
              }}
            >
              <MenuItem value="pressure">מד לחץ</MenuItem>
              <MenuItem value="flow">מד ספיקה</MenuItem>
              <MenuItem value="volume">מד נפח</MenuItem>
              <MenuItem value="all">הכל</MenuItem>
            </Select>
          </FormControl>
          <AppDateRangePicker
            value={{
              from: startDate ? startDate.toDate() : null,
              to: endDate ? endDate.toDate() : null,
            }}
            onChange={range => {
              setStartDate(range.from ? dayjs(range.from) : null)
              setEndDate(range.to ? dayjs(range.to) : null)
            }}
            disableFuture
            clearable
          />

          <Button
            onClick={handleSearch}
            variant="contained"
            size="small"
            sx={{
              border: `1px solid ${colors.btnBorder}`,
              bgcolor: colors.btnBg,
              color: colors.btnText,
              boxShadow: 'none',
              '&:hover': { bgcolor: colors.btnHover, boxShadow: 'none' },
            }}
          >
            חיפוש
          </Button>

          <Box sx={{ flexGrow: 1 }} />
        </Box>
        <Button
          onClick={() => {
            navigate('/sensors')
          }}
          variant="contained"
          sx={{
            height: '34px',
            gap: '10px',
            flexShrink: 0,
            padding: '2px 13px',
            borderRadius: '4.5px',
            border: `1px solid ${colors.outlineBtnBorder}`,
            background: colors.btnBg,
          }}
        >
          <Typography
            sx={{
              color: colors.btnText,
              textAlign: 'right',
              fontFamily: 'Rubik, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 300,
              lineHeight: '100%',
            }}
          >
            חזרה לטבלה
          </Typography>
        </Button>
      </Box>
      <Box
        sx={{
          direction: 'rtl',
          width: '100%',
          height: '70vh',
          px: 2,
          gap: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '600px 1fr' },
          alignItems: 'stretch',
        }}
      >
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${colors.paperBorder}`, bgcolor: colors.paperBg, borderRadius: '8px', p: 2 }}
        >
          <Typography
            sx={{
              pb: 1,
              mb: 1,
              color: colors.titleColor,
              borderBottom: `1px solid ${colors.titleBorder}`,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            מקרא הגרף על פי צבעי הטבלה
          </Typography>

          <Box sx={{ width: '100%', height: 420 }}>
            <LineCharts
              startDate={startDate ?? dayjs()}
              endDate={endDate ?? dayjs()}
              chartData={chartSeries}
              loading={sensorsLoading}
            />
          </Box>
        </Paper>

        {/* <Paper elevation={0} sx={{ p: 1, bgcolor: '#FAFBFD' }}> */}
        <Box>
          <DataGridPremiumDemo
            apiRefExternal={apiRefExternal}
            columns={graphColumnsConfig(colorsMap)}
            rows={allowedRows}
            hideFooter
            headerPosition="bottom"
            pageSize={25}
            width="100%"
            checkboxSelection
            rowSelectionModel={rowSelectionModel as any}
            onRowSelectionModelChange={(model: any) =>
              setRowSelectionModel(model as RowSelectionModelV7)
            }
          />
        </Box>

        {/* </Paper> */}
      </Box>
    </Box>
  )
}

export default SensorsGraph
